<script>
import Swal from "sweetalert2";
import PacientController from "@/controller/PacientController";

const AXIS_COLORS = [
  "#556ee6",
  "#34c38f",
  "#f46a6a",
  "#50a5f1",
  "#f1b44c",
  "#6f42c1",
  "#20c997",
  "#fd7e14",
];

export default {
  props: {
    pacientId: { type: Number, required: true },
    initialYear: { type: [String, Number], default: null },
  },
  data() {
    const nowYear = new Date().getFullYear();
    return {
      loading: false,
      error: null,
      chart: null,
      year: Number(this.initialYear) || nowYear,
      years: Array.from({ length: 6 }, (_, i) => nowYear - i),
    };
  },
  computed: {
    isProcessing() {
      return this.chart?.status === "processing";
    },
    isReady() {
      return this.chart?.status === "ready";
    },
    isEmpty() {
      return this.chart?.status === "empty";
    },
    xCategories() {
      return (this.chart?.series_by_month || []).map((row) => row.month);
    },
    lineSeries() {
      if (!this.isReady) return [];
      const rows = this.chart.series_by_month || [];
      const axes = this.chart.axes || [];
      return axes.map((axis) => ({
        name: axis.label,
        data: rows.map((row) => row[axis.key]),
      }));
    },
    lineOptions() {
      return {
        chart: { toolbar: { show: true } },
        stroke: { width: 2, curve: "smooth" },
        colors: AXIS_COLORS,
        xaxis: { categories: this.xCategories },
        yaxis: { min: 0, max: 10, tickAmount: 5 },
        tooltip: { shared: true },
      };
    },
    radarSeries() {
      if (!this.isReady || !this.chart.radar_start_vs_end) return [];
      const radar = this.chart.radar_start_vs_end;
      const axes = this.chart.axes || [];
      return [
        {
          name: radar.start_label || "Início do ano",
          data: axes.map((axis) => radar.start?.[axis.key]),
        },
        {
          name: radar.end_label || "Fim do ano",
          data: axes.map((axis) => radar.end?.[axis.key]),
        },
      ];
    },
    radarOptions() {
      return {
        chart: { toolbar: { show: false } },
        xaxis: { categories: (this.chart?.axes || []).map((a) => a.label) },
        yaxis: { min: 0, max: 10, tickAmount: 5 },
        colors: ["#556ee6", "#34c38f"],
      };
    },
  },
  created() {
    this.PacientController = new PacientController();
  },
  mounted() {
    this.fetchChart();
  },
  methods: {
    async fetchChart(force = false) {
      this.loading = true;
      this.error = null;
      try {
        const data = await this.PacientController.getEvolutionChart(this.pacientId, {
          year: this.year,
          force,
        });
        this.chart = data;
        if (this.isProcessing) {
          Swal.fire({
            icon: "info",
            title: "Processando gráfico",
            text: data.message || "Os dados serão processados e você receberá uma notificação quando estiver pronto.",
          });
        }
      } catch (err) {
        this.error = err?.response?.data?.message || "Não foi possível carregar o gráfico de evolução.";
      } finally {
        this.loading = false;
      }
    },
    reprocess() {
      this.fetchChart(true);
    },
  },
};
</script>

<template>
  <div>
    <div class="d-flex justify-content-between mb-3">
      <div class="d-flex align-items-center">
        <label class="mb-0 mr-2">Ano</label>
        <b-form-select v-model="year" :options="years" @change="fetchChart" />
      </div>
      <b-button variant="outline-primary" size="sm" @click="reprocess">Reprocessar agora</b-button>
    </div>

    <div v-if="loading" class="text-center py-4">
      <b-spinner class="mr-2"></b-spinner>
      <strong>Carregando gráfico...</strong>
    </div>

    <b-alert v-else-if="error" show variant="danger">{{ error }}</b-alert>

    <b-alert v-else-if="isProcessing" show variant="info">
      Estamos analisando {{ chart.recurrences_total || 0 }} evoluções do ano {{ year }}.
      Tempo estimado: ~{{ chart.estimated_seconds || "..." }} segundos.
      Você receberá uma notificação quando estiver pronto.
    </b-alert>

    <b-alert v-else-if="isEmpty" show variant="light">
      Sem evoluções registradas em {{ year }}.
    </b-alert>

    <div v-else-if="isReady">
      <div class="card mb-3">
        <div class="card-header">Evolução por eixo no ano</div>
        <div class="card-body">
          <apexchart height="320" type="line" :options="lineOptions" :series="lineSeries" />
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-header">Comparativo início vs fim do ano</div>
        <div class="card-body">
          <apexchart height="320" type="radar" :options="radarOptions" :series="radarSeries" />
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-header">Tendências</div>
        <div class="card-body">
          <div class="row">
            <div v-for="trend in chart.trends || []" :key="trend.axis" class="col-md-3 col-sm-6 mb-2">
              <div class="border rounded p-2 h-100">
                <div class="font-weight-bold">{{ trend.axis }}</div>
                <div>Slope: {{ trend.slope }}</div>
                <div>Direção: {{ trend.direction }}</div>
                <div>Confiança: {{ trend.confidence }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card mb-3">
        <div class="card-header">Destaques da IA</div>
        <div class="card-body">
          <ul class="mb-0">
            <li v-for="(item, idx) in chart.highlights || []" :key="idx">{{ item }}</li>
          </ul>
        </div>
      </div>

      <p class="text-muted small mb-0">
        {{ chart.disclaimer }}
      </p>
    </div>
  </div>
</template>
